/**
 * Google Identity Services — the "Entrar com Google" button (02-requirements.md
 * §Admin Authentication, Google-only since 2026-09-27, the same way Camp Mobi's
 * panel signs in). The button hands back a Google-signed ID token; the API
 * checks its signature, audience and `email_verified`, then its own allowlist.
 *
 * The script is Google's and has to come from Google, so it is the panel's one
 * third-party script. It is injected by the login screen on demand, never from
 * `index.html`: a signed-in screen never loads it.
 */
const SCRIPT_URL = 'https://accounts.google.com/gsi/client';

export interface GoogleCredentialResponse {
  credential: string;
}

export interface GoogleIdentityApi {
  initialize(options: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    ux_mode?: 'popup' | 'redirect';
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }): void;
  renderButton(
    parent: HTMLElement,
    options: {
      type?: 'standard' | 'icon';
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      size?: 'large' | 'medium' | 'small';
      text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
      shape?: 'rectangular' | 'pill' | 'circle' | 'square';
      width?: number;
      locale?: string;
    },
  ): void;
  disableAutoSelect(): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleIdentityApi } };
  }
}

let loading: Promise<GoogleIdentityApi> | null = null;

/** Loads the client once per page; a failed load may be retried by the next call. */
export function loadGoogleIdentity(): Promise<GoogleIdentityApi> {
  const ready = window.google?.accounts?.id;
  if (ready) return Promise.resolve(ready);
  if (loading) return loading;

  loading = new Promise<GoogleIdentityApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const api = window.google?.accounts?.id;
      if (api) resolve(api);
      else reject(new Error('google_unavailable'));
    };
    script.onerror = () => {
      script.remove();
      reject(new Error('google_unavailable'));
    };
    document.head.appendChild(script);
  }).catch((cause: unknown) => {
    loading = null;
    throw cause;
  });
  return loading;
}
